// --- React · 라우팅 ---
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 ---
import { css } from '@emotion/react';
import { useTheme } from '@emotion/react';
import type { AppTheme } from '@/theme/theme';

// --- API ---
import { useVerifyEmail } from '../../api/auth/useEmailVerification';

// --- 유효성 검사 · 에러 파싱 ---
import { parseApiError } from '@/utils/errors';

// --- 스토어 ---
import { useNotificationStore } from '../../store/notificationStore';

// --- 타입 ---
import type { ApiError } from '@packages/types/common/dto';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// ==================== 컴포넌트 ====================

const VerifyEmail: React.FC = () => {
  const theme = useTheme() as AppTheme;
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotificationStore();

  // ==================== 상태 관리 ====================

  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const hasVerified = useRef(false);
  const emailRef = useRef<string>('');

  // ==================== API 훅 ====================

  /**
   * 이메일 인증 뮤테이션.
   * 성공/실패/만료 각 케이스에 따라 UI 상태를 업데이트하고
   * 적절한 페이지로 리다이렉트한다.
   */
  const verifyEmailMutation = useVerifyEmail({
    onSuccess: response => {
      setIsVerified(true);
      const isAlreadyVerified = response.message === 'auth.already_verified';
      const title = isAlreadyVerified 
        ? t(i18nKeys.auth.email_already_registered) 
        : t(i18nKeys.auth.email_verified);
      const message = isAlreadyVerified
        ? t(i18nKeys.auth.email_already_registered)
        : t(i18nKeys.auth.email_verified);

      showSuccess(title, message);

      // 3초 후 회원가입 페이지로 이동 (URL 쿼리 파라미터와 state 모두 전달)
      setTimeout(() => {
        navigate(`/register?email=${encodeURIComponent(emailRef.current)}&verified=true`, {
          state: {
            email: emailRef.current,
            isEmailVerified: true,
          },
        });
      }, 3000);
    },
    onError: (error: unknown) => {
      console.error('❌ [VerifyEmail] onError called:', error);
      const apiError = error as ApiError;
      const errorData = apiError.response?.data;

      // 만료된 토큰인 경우 — 메시지 표시 후 회원가입 페이지로 리다이렉션
      if (errorData?.code === 'VERIFICATION_EXPIRED' || errorData?.code === 'TOKEN_EXPIRED') {
        setIsVerified(false);
        setIsVerifying(false);
        setIsExpired(true);

        // 5초 후 회원가입 페이지로 이동 (메시지를 충분히 볼 수 있도록)
        setTimeout(() => {
          navigate('/register', {
            replace: true,
            state: {
              email: emailRef.current,
              expired: true,
            },
          });
        }, 5000);
        return;
      }

      const e = parseApiError(error);
      showError(e.title, e.message);
      setIsVerified(false);
      setIsVerifying(false);
      setTimeout(() => {
        navigate('/register', { replace: true });
      }, 2000);
    },
    onSettled: () => {
      setIsVerifying(false);
    },
  });

  // ==================== 이펙트 ====================

  /**
   * URL 파라미터에서 token · email을 추출하여 이메일 인증 API 호출.
   * 파라미터가 없으면 에러 표시 후 회원가입 페이지로 리다이렉트.
   */
  useEffect(() => {
    if (hasVerified.current) return;

    const verifyEmail = async () => {
      hasVerified.current = true;
      const token = searchParams.get('token');
      const email = searchParams.get('email');

      if (!token || !email) {
        showError(
          t(i18nKeys.auth.invalid_link), 
          t(i18nKeys.auth.missing_token_or_email)
        );
        navigate('/register', { replace: true });
        return;
      }

      emailRef.current = email;

      verifyEmailMutation.mutate({ email, token });
    };

    verifyEmail();
  }, [searchParams, navigate, showError]);

  // ==================== 렌더링 ====================

  const styles = {
    container: css`
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #3b82f6, #2563eb, #1d4ed8);
    `,
    card: css`
      background: white;
      border-radius: 1rem;
      padding: 3rem;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      text-align: center;
      max-width: 28rem;
      width: 100%;
      margin: 1rem;
    `,
    title: css`
      font-size: 1.875rem;
      font-weight: bold;
      color: ${theme.colors.text.primary};
      margin-bottom: 1rem;
    `,
    message: css`
      font-size: 1rem;
      color: ${theme.colors.text.secondary};
      margin-bottom: 2rem;
      line-height: 1.6;
    `,
    spinner: css`
      width: 3rem;
      height: 3rem;
      border: 4px solid #e5e7eb;
      border-top: 4px solid #3b82f6;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 2rem;

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    `,
    successIcon: css`
      width: 4rem;
      height: 4rem;
      color: ${theme.colors.status.stable.fill.icon};
      margin: 0 auto 2rem;
    `,
    redirectMessage: css`
      font-size: 0.875rem;
      color: ${theme.colors.text.secondary};
      margin-top: 1rem;
    `,
  };

  return (
    <div css={styles.container}>
      <div css={styles.card}>
        {/* 인증 진행 중 — 로딩 스피너 */}
        {isVerifying ? (
          <>
            <div css={styles.spinner}></div>
            <h1 css={styles.title}>{t(i18nKeys.auth.validating_token)}</h1>
            <p css={styles.message}>{t(i18nKeys.auth.email_verification_sent)}</p>
          </>
        ) : isVerified ? (
          /* 인증 성공 — 체크 아이콘 + 리다이렉트 안내 */
          <>
            <svg css={styles.successIcon} fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <h1 css={styles.title}>{t(i18nKeys.auth.email_verified)}</h1>
            <p css={styles.message}>
              {t(i18nKeys.auth.email_verified)}
            </p>
            <p css={styles.redirectMessage}>{t(i18nKeys.auth.redirecting_to_sign_up)}</p>
          </>
        ) : isExpired ? (
          /* 토큰 만료 — 경고 아이콘 + 리다이렉트 안내 */
          <>
            <svg
              css={css`
                width: 4rem;
                height: 4rem;
                color: ${theme.colors.status.caution.fill.icon};
                margin: 0 auto 2rem;
              `}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <h1 css={styles.title}>{t(i18nKeys.auth.verification_expired)}</h1>
            <p css={styles.message}>{t(i18nKeys.auth.verification_token_expired)}</p>
            <p css={styles.redirectMessage}>{t(i18nKeys.auth.redirecting_to_sign_up)}</p>
          </>
        ) : (
          /* 인증 실패 — 유효하지 않은 토큰 */
          <>
            <h1 css={styles.title}>{t(i18nKeys.auth.invalid_verification_token)}</h1>
            <p css={styles.message}>{t(i18nKeys.auth.token_expired_or_invalid)}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
